import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import HeroSettings from "./HeroSettings";
import ProjectRow from "./ProjectRow";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("projects");

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: l }] = await Promise.all([api.get("/projects"), api.get("/leads")]);
      setProjects(p);
      setLeads(l);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("ai_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); load(); })
      .catch(() => { sessionStorage.removeItem("ai_token"); navigate("/admin/login"); });
  }, [navigate, load]);

  const logout = () => {
    sessionStorage.removeItem("ai_token");
    navigate("/admin/login");
  };

  const toggleFeature = async (p) => {
    try {
      await api.post(`/projects/${p.id}/feature`, { featured: !p.featured });
      setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = async (index, dir) => {
    const ids = projects.map((p) => p.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    const reordered = ids.map((id) => projects.find((p) => p.id === id));
    setProjects(reordered);
    try {
      await api.post("/projects/reorder", { ids });
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.title_it}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Progetto eliminato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center bg-[#080808]"><p className="font-mono text-xs text-[#827D72]">…</p></div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#080808] pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C8A86B]">Area Riservata</p>
            <h1 className="mt-2 font-serif text-3xl text-[#F3EFE6]">Portfolio Manager</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="inline-flex items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#F3EFE6]">
              <ExternalLink size={13} /> Sito
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C25D53]/60 hover:text-[#C25D53]">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-2 border-b border-[#1C1A17]">
          {["projects", "leads", "hero"].map((tb) => (
            <button
              key={tb}
              data-testid={`admin-tab-${tb}`}
              onClick={() => setTab(tb)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === tb ? "border-b border-[#C8A86B] text-[#C8A86B]" : "text-[#827D72] hover:text-[#F3EFE6]"}`}
            >
              {tb === "projects" ? `Progetti (${projects.length})` : tb === "leads" ? `Download Leads (${leads.length})` : "Hero Video"}
            </button>
          ))}
        </div>

        {tab === "hero" && <HeroSettings />}

        {tab === "projects" && (
          <div className="mt-8">
            <Link
              to="/admin/projects/new"
              data-testid="new-project-btn"
              className="inline-flex items-center gap-2 bg-[#C8A86B] px-5 py-3 text-xs uppercase tracking-[0.15em] text-[#080808] transition-colors hover:bg-[#D8BD85]"
            >
              <Plus size={14} /> Nuovo Progetto
            </Link>

            <div className="mt-6 flex flex-col gap-3">
              {projects.map((p, i) => (
                <ProjectRow key={p.id} p={p} i={i} total={projects.length} onMove={move} onToggleFeature={toggleFeature} onRemove={remove} />
              ))}
              {projects.length === 0 && (
                <p className="border border-dashed border-[#262420] py-16 text-center text-sm text-[#827D72]">Nessun progetto. Crea il primo.</p>
              )}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-8 flex flex-col gap-2">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-row-${l.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-[#1C1A17] bg-[#0F0F0E] px-4 py-3">
                <div>
                  <p className="text-sm text-[#F3EFE6]">{l.name} {l.firm && <span className="text-[#827D72]">— {l.firm}</span>}</p>
                  <p className="text-xs font-mono text-[#827D72]">{l.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono text-[#A39E93]">{l.file_name}</p>
                  <p className="text-[10px] font-mono text-[#666157]">{new Date(l.created_at).toLocaleDateString("it-IT")}</p>
                </div>
              </div>
            ))}
            {leads.length === 0 && (
              <p className="border border-dashed border-[#262420] py-16 text-center text-sm text-[#827D72]">Nessun download registrato.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
